'use client'

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { UIMessage, UseChatHelpers } from '@ai-sdk/react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { UIMessagePart, UIDataTypes, UITools } from 'ai'
import {
  File,
  FileCode2,
  FileSpreadsheet,
  FileText,
  FilePenLine,
  Globe,
  Image,
  Search,
  Shield,
  ClipboardList,
  Cloud,
  ScanSearch,
  Users,
  GitCompare,
  HelpCircle,
  Eye,
  Wrench,
  BookOpen,
  ClipboardCheck,
  FileSearch,
  LayoutGrid,
  CalendarClock,
  Camera,
  Database,
  Sparkles,
  Mail,
  ListTodo,
  Github,
  Loader2,
  CircleAlert,
  RefreshCw,
} from 'lucide-react'
import { useStickToBottomContext } from 'use-stick-to-bottom'
import { cn } from '@/lib/utils'
import { ConversationContent } from '@/components/ai-elements/conversation'
import { Message, MessageContent } from '@/components/ai-elements/message'
import {
  Reasoning,
  ReasoningContent,
  ReasoningTrigger,
} from '@/components/ai-elements/reasoning'
import {
  BeautifulUIPixelGridLoader,
  Loader,
} from '@/components/magicui/animated-shiny-text'
import { ChatStoppedContext } from './chat-stopped-context'
import { ResponseWithCitations } from './components/ResponseWithCitations'
import { ApplyableResponse } from './components/messages/ApplyableResponse'
import { ApprovalRequestCard } from './components/messages/ApprovalRequestCard'
import { MessageActions } from './components/messages/MessageActions'
import AddSourceMessage from './components/messages/tools/AddSourceMessage'
import ArticleGenerationMessage from './components/messages/tools/ArticleGenerationMessage'
import CitationSearchMessage from './components/messages/tools/CitationSearchMessage'
import FindInternalDocMessage from './components/messages/tools/FindInternalDocMessage'
import GetStepDOMMessage from './components/messages/tools/GetStepDOMMessage'
import GetStepScreenshotMessage from './components/messages/tools/GetStepScreenshotMessage'
import GlossarySearchMessage from './components/messages/tools/GlossarySearchMessage'
import { IntegrationConnectMessage } from './components/messages/tools/IntegrationConnectMessage'
import {
  GeneratePolicyMessage,
  UpdatePolicySectionMessage,
  RecordEvidenceMessage,
  CheckPolicyComplianceMessage,
  BuildPolicyPlanMessage,
  GeneratePlanQuestionsMessage,
  GetCloudScanEvidenceMessage,
  GetPopulationListsMessage,
  DelegateToScannerAgentMessage,
  CheckPolicyConsistencyMessage,
  RunGuidedTemplateMessage,
  GetPageContentMessage,
  TriageDraftClaimMessage,
  RecordClaimMessage,
  AnalyzeGapMessage,
  LookupCisBenchmarkMessage,
  CheckPolicyQualityMessage,
  CheckPolicyMessage,
  RecordClaimsMessage,
  DelegateToMatrixAgentMessage,
  DelegateToSchedulerAgentMessage,
  GetProgramStateMessage,
  GetCompanyReadinessMessage,
  AskPolicyQuestionMessage,
  UpdateProgramStateMessage,
  ToolDebugPanel,
} from './components/messages/tools/PolicyToolMessages'
import {
  readToolState,
  ToolStatusLine,
} from './components/messages/tools/perAction/_base'
import { PresentOptionsMessage } from './components/messages/tools/PresentOptionsMessage'
import { ProposeApplyBlocksMessage } from './components/messages/tools/ProposeApplyBlocksMessage'
import { RunbookCatalogToolMessage } from './components/messages/tools/RunbookCatalogMessage'
import { AskQuestionsMessage } from './components/messages/tools/AskQuestionsMessage'
import { ProposeRunbookMessage } from './components/messages/tools/RunbookDraftToolMessages'
import {
  MatrixToolMessage,
  SchedulingToolMessage,
  PoliciesToolMessage,
  ProgramToolMessage,
  EvidenceToolMessage,
  RemediationToolMessage,
  ProposeRemediationToolMessage,
  ExecuteRemediationPlanToolMessage,
  UndoRemediationToolMessage,
  ScanningToolMessage,
  CisToolMessage,
  ClaimsToolMessage,
  ArtifactsToolMessage,
  ChecksToolMessage,
  RisksToolMessage,
  TicketsToolMessage,
  VendorsToolMessage,
  AssetsToolMessage,
  IntegrationsToolMessage,
  PopulationToolMessage,
  UiToolMessage,
  DocsToolMessage,
  DocumentsToolMessage,
  SaveOnboardingDataToolMessage,
  CompleteOnboardingToolMessage,
  ConnectGithubToolMessage,
  ConnectCloudToolMessage,
  TriggerGithubScanToolMessage,
  UploadControlMatrixToolMessage,
  PresentComplianceRolesToolMessage,
  SendEmailToolMessage,
  TestsToolMessage,
  RunbookToolMessage,
  RunbookExecutionToolMessage,
  ShowArtifactToolMessage,
  PresentCtaButtonMessage,
} from './components/messages/tools/ScreenataChatToolMessages'
import { PresentCaptureFlowToolMessage } from './components/messages/tools/PresentCaptureFlowToolMessage'
import { PresentRemedyFlowToolMessage } from './components/messages/tools/PresentRemedyFlowToolMessage'
import SearchKnowledgeMessage from './components/messages/tools/SearchKnowledgeMessage'
import TopicSuggestionMessage from './components/messages/tools/TopicSuggestionMessage'
import TopicSuggestionWithContentMessage from './components/messages/tools/TopicSuggestionWithContentMessage'
import { VendorPickerMessage } from './components/messages/tools/VendorPickerMessage'
import WebSearchMessage from './components/messages/tools/WebSearchMessage'
import {
  ChatAttachmentPreviewDialog,
  type ChatAttachmentPreviewFile,
} from './components/ChatAttachmentPreviewDialog'
import { AttachmentPill } from './components/AttachmentPill'
import { getChatAttachmentPresentation } from './lib/attachment-presentation'
// Per-action tool message components (RFC-170 bag-tool decomposition).
// Each per-action tool (e.g. `runbookSearch`) has its own React component.
// Components live grouped by namespace under tools/perAction/. See the
// index there for the dispatch table.
import {
  PER_ACTION_TOOL_COMPONENTS,
  PER_ACTION_TOOL_ICONS,
} from './components/messages/tools/perAction'

interface MessagesProps {
  messages: UIMessage[]
  status: UseChatHelpers['status']
  messageSources?: Map<string, any[]> // RFC-070: Sources by message ID
  chatEventScope?: string | null
  onRegenerate?: () => void
}

interface ChatMessageItemProps {
  message: UIMessage
  isLastAssistant: boolean
  isLatestTriggerGithubScanMessage: boolean
  isReady: boolean
  isStreamingMessage: boolean
  isActiveResponse: boolean
  isFirstInGroup: boolean
  sources: any[]
  chatEventScope?: string | null
  onRegenerate?: () => void
}

function UserMessageAttachmentPill({
  attachment,
}: {
  attachment: ChatAttachmentPreviewFile
}) {
  const presentation = getChatAttachmentPresentation(
    attachment.type,
    attachment.name
  )

  return (
    <AttachmentPill
      icon={<UserMessageAttachmentIcon kind={presentation.kind} />}
      name={attachment.name}
    />
  )
}

function UserMessageAttachmentIcon({
  kind,
}: {
  kind: ReturnType<typeof getChatAttachmentPresentation>['kind']
}) {
  const className = 'text-muted-foreground h-3.5 w-3.5'
  if (kind === 'xlsx' || kind === 'csv') {
    return <FileSpreadsheet className={className} />
  }
  if (kind === 'json' || kind === 'md' || kind === 'txt') {
    return <FileCode2 className={className} />
  }
  if (kind === 'pdf' || kind === 'docx') {
    return <FileText className={className} />
  }
  return <File className={className} />
}

const EMPTY_SOURCES: any[] = []

const TOOL_MESSAGE_COMPONENTS = {
  // used for generating from flow
  topic_suggestion: TopicSuggestionMessage,
  // used for generating from chat
  suggest_topic_with_content: TopicSuggestionWithContentMessage,
  article_generation: ArticleGenerationMessage,
  citationSearch: CitationSearchMessage,
  getStepDom: GetStepDOMMessage,
  getStepScreenshot: GetStepScreenshotMessage,
  glossarySearch: GlossarySearchMessage,
  searchKnowledge: FindInternalDocMessage,
  addSource: AddSourceMessage,
  webSearch: WebSearchMessage,
  presentOptions: PresentOptionsMessage,
  askQuestions: AskQuestionsMessage,
  proposeRunbook: ProposeRunbookMessage,
  runbookCatalog: RunbookCatalogToolMessage,
  presentQuestion: PresentOptionsMessage,
  presentVendorPicker: VendorPickerMessage,
  generatePolicy: GeneratePolicyMessage,
  updatePolicySection: UpdatePolicySectionMessage,
  recordEvidence: RecordEvidenceMessage,
  checkPolicyCompliance: CheckPolicyComplianceMessage,
  buildPolicyPlan: BuildPolicyPlanMessage,
  generatePlanQuestions: GeneratePlanQuestionsMessage,
  getCloudScanEvidence: GetCloudScanEvidenceMessage,
  getPopulationLists: GetPopulationListsMessage,
  delegateToScannerAgent: DelegateToScannerAgentMessage,
  checkPolicyConsistency: CheckPolicyConsistencyMessage,
  runGuidedTemplate: RunGuidedTemplateMessage,
  getPageContent: GetPageContentMessage,
  triageDraftClaim: TriageDraftClaimMessage,
  recordClaim: RecordClaimMessage,
  analyzeGap: AnalyzeGapMessage,
  lookupCisBenchmark: LookupCisBenchmarkMessage,
  checkPolicyQuality: CheckPolicyQualityMessage,
  checkPolicy: CheckPolicyMessage,
  recordClaims: RecordClaimsMessage,
  delegateToMatrixAgent: DelegateToMatrixAgentMessage,
  delegateToSchedulerAgent: DelegateToSchedulerAgentMessage,
  getProgramState: GetProgramStateMessage,
  getCompanyReadiness: GetCompanyReadinessMessage,
  askPolicyQuestion: AskPolicyQuestionMessage,
  updateProgramState: UpdateProgramStateMessage,
  // Screenata chat agent tools (action-based, multi-action per tool)
  matrix: MatrixToolMessage,
  scheduling: SchedulingToolMessage,
  policies: PoliciesToolMessage,
  program: ProgramToolMessage,
  evidence: EvidenceToolMessage,
  claims: ClaimsToolMessage,
  remediation: RemediationToolMessage,
  remediations: RemediationToolMessage,
  findings: RemediationToolMessage,
  proposeRemediation: ProposeRemediationToolMessage,
  executeRemediationPlan: ExecuteRemediationPlanToolMessage,
  undoRemediation: UndoRemediationToolMessage,
  scanning: ScanningToolMessage,
  checks: ChecksToolMessage,
  artifacts: ArtifactsToolMessage,
  cis: CisToolMessage,
  risks: RisksToolMessage,
  tickets: TicketsToolMessage,
  vendors: VendorsToolMessage,
  assets: AssetsToolMessage,
  integrations: IntegrationsToolMessage,
  population: PopulationToolMessage,
  ui: UiToolMessage,
  docs: DocsToolMessage,
  documents: DocumentsToolMessage,
  // Onboarding chat agent tools
  saveOnboardingData: SaveOnboardingDataToolMessage,
  completeOnboarding: CompleteOnboardingToolMessage,
  connectGithub: ConnectGithubToolMessage,
  connectCloud: ConnectCloudToolMessage,
  triggerGithubScan: TriggerGithubScanToolMessage,
  uploadControlMatrix: UploadControlMatrixToolMessage,
  presentComplianceRoles: PresentComplianceRolesToolMessage,
  showArtifact: ShowArtifactToolMessage,
  presentCtaButton: PresentCtaButtonMessage,
  presentCaptureFlow: PresentCaptureFlowToolMessage,
  presentRemedyFlow: PresentRemedyFlowToolMessage,
  sendEmail: SendEmailToolMessage,
  tests: TestsToolMessage,
  runbook: RunbookToolMessage,
  runbookExecution: RunbookExecutionToolMessage,
  // Per-action tools (RFC-170 bag-tool decomposition).
  // Each per-tool config (labels, detail, icon, optional bag delegation)
  // lives in `./components/messages/tools/perActionToolRegistry.tsx`.
  ...PER_ACTION_TOOL_COMPONENTS,
} as const

export const CHAT_TOOL_UI_NAMES = Object.keys(TOOL_MESSAGE_COMPONENTS).sort()

const IconClass = 'h-[16px] w-[16px] [&_svg]:size-[16px]'

const TOOL_MESSAGE_ICONS = {
  topic_suggestion: <Search className={IconClass} strokeWidth={2} />,
  suggest_topic_with_content: <Search className={IconClass} strokeWidth={2} />,
  article_generation: <FilePenLine className={IconClass} strokeWidth={2} />,
  citationSearch: <Search className={IconClass} strokeWidth={2} />,
  getStepDom: <Search className={IconClass} strokeWidth={2} />,
  glossarySearch: <Search className={IconClass} strokeWidth={2} />,
  findInternalDoc: <Search className={IconClass} strokeWidth={2} />,
  addSource: <Search className={IconClass} strokeWidth={2} />,
  webSearch: <Globe className={IconClass} strokeWidth={2} />,
  getStepScreenshot: <Image className={IconClass} strokeWidth={2} />,
  generatePolicy: <FileText className={IconClass} strokeWidth={2} />,
  updatePolicySection: <FileText className={IconClass} strokeWidth={2} />,
  recordEvidence: <Shield className={IconClass} strokeWidth={2} />,
  checkPolicyCompliance: <Shield className={IconClass} strokeWidth={2} />,
  claims: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  buildPolicyPlan: <ClipboardList className={IconClass} strokeWidth={2} />,
  generatePlanQuestions: <HelpCircle className={IconClass} strokeWidth={2} />,
  getCloudScanEvidence: <Cloud className={IconClass} strokeWidth={2} />,
  getPopulationLists: <Users className={IconClass} strokeWidth={2} />,
  delegateToScannerAgent: <ScanSearch className={IconClass} strokeWidth={2} />,
  checkPolicyConsistency: <GitCompare className={IconClass} strokeWidth={2} />,
  runGuidedTemplate: <Cloud className={IconClass} strokeWidth={2} />,
  getPageContent: <Eye className={IconClass} strokeWidth={2} />,
  triageDraftClaim: <Wrench className={IconClass} strokeWidth={2} />,
  recordClaim: <Shield className={IconClass} strokeWidth={2} />,
  analyzeGap: <ScanSearch className={IconClass} strokeWidth={2} />,
  lookupCisBenchmark: <BookOpen className={IconClass} strokeWidth={2} />,
  checkPolicyQuality: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  checkPolicy: <FileSearch className={IconClass} strokeWidth={2} />,
  recordClaims: <Shield className={IconClass} strokeWidth={2} />,
  delegateToMatrixAgent: <LayoutGrid className={IconClass} strokeWidth={2} />,
  delegateToSchedulerAgent: (
    <CalendarClock className={IconClass} strokeWidth={2} />
  ),
  getProgramState: <ClipboardList className={IconClass} strokeWidth={2} />,
  getCompanyReadiness: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  askPolicyQuestion: <HelpCircle className={IconClass} strokeWidth={2} />,
  updateProgramState: <ClipboardList className={IconClass} strokeWidth={2} />,
  // Screenata chat agent tools
  matrix: <LayoutGrid className={IconClass} strokeWidth={2} />,
  scheduling: <CalendarClock className={IconClass} strokeWidth={2} />,
  policies: <FileText className={IconClass} strokeWidth={2} />,
  program: <ClipboardList className={IconClass} strokeWidth={2} />,
  evidence: <Database className={IconClass} strokeWidth={2} />,
  remediation: <Wrench className={IconClass} strokeWidth={2} />,
  remediations: <Wrench className={IconClass} strokeWidth={2} />,
  findings: <Wrench className={IconClass} strokeWidth={2} />,
  scanning: <ScanSearch className={IconClass} strokeWidth={2} />,
  cis: <Shield className={IconClass} strokeWidth={2} />,
  risks: <Shield className={IconClass} strokeWidth={2} />,
  vendors: <Database className={IconClass} strokeWidth={2} />,
  assets: <Database className={IconClass} strokeWidth={2} />,
  integrations: <Github className={IconClass} strokeWidth={2} />,
  population: <Users className={IconClass} strokeWidth={2} />,
  ui: <Eye className={IconClass} strokeWidth={2} />,
  docs: <BookOpen className={IconClass} strokeWidth={2} />,
  documents: <FileText className={IconClass} strokeWidth={2} />,
  sendEmail: <Mail className={IconClass} strokeWidth={2} />,
  tests: <ListTodo className={IconClass} strokeWidth={2} />,
  runbook: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  runbookExecution: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  askQuestions: <HelpCircle className={IconClass} strokeWidth={2} />,
  proposeRunbook: <ClipboardCheck className={IconClass} strokeWidth={2} />,
  runbookCatalog: <BookOpen className={IconClass} strokeWidth={2} />,
  presentCaptureFlow: <Camera className={IconClass} strokeWidth={2} />,
  presentRemedyFlow: <Wrench className={IconClass} strokeWidth={2} />,
  // Per-action icons (RFC-170 bag-tool decomposition).
  // Sourced from perActionToolRegistry — each tool config carries its own icon.
  ...PER_ACTION_TOOL_ICONS,
} as const

// ── Helpers ────────────────────────────────────────────────────────────────

function isToolPart(part: UIMessagePart<UIDataTypes, UITools>): boolean {
  return part.type.startsWith('tool-') || part.type === 'tool-invocation'
}

/** Reasoning shimmer or in-flight tool rows already signal "working". */
function assistantMessageHasActiveStreamIndicator(message: UIMessage): boolean {
  if (message.role !== 'assistant') return false

  for (const part of message.parts ?? []) {
    if (part.type === 'reasoning' && (part as { state?: string }).state === 'streaming') {
      return true
    }
    if (isToolPart(part)) {
      const toolState = readToolState(part)
      if (!toolState.isComplete && !toolState.awaitingApproval) {
        return true
      }
    }
  }

  return false
}

function shouldShowGlobalStreamDots(
  messages: UIMessage[],
  lastMessageId: string | undefined,
  status: MessagesProps['status'],
): boolean {
  if (status !== 'streaming' && status !== 'submitted') return false

  const activeMessage =
    (lastMessageId
      ? messages.find((message) => message.id === lastMessageId)
      : undefined) ?? messages.at(-1)

  // Once an assistant bubble exists, the in-message status row owns the
  // loading indicator — a second row at the list bottom causes a jump.
  if (activeMessage?.role === 'assistant') {
    return false
  }

  return true
}

/**
 * Split a reasoning summary into a short headline (for the collapsed
 * trigger) and the remaining body (for the expanded content).
 *
 * OpenAI's reasoning summaries land as markdown — typically a bolded
 * first line ("**Checking policy status**") followed by the body, or a
 * heading ("## Checking policy status"). When the first line is a genuine
 * markdown title (bold or heading) the trigger already renders it, so the
 * expanded body strips it to avoid showing the same title twice
 * (`isStructured: true`).
 *
 * When the first line is plain prose ("The test has these properties:"),
 * it is NOT a title — stripping it would make the expanded body start
 * mid-thought (e.g. with a code block). In that case `isStructured` is
 * false; callers keep the full text in the body so the collapsed preview
 * and the expanded content start with the same text.
 */
function splitReasoningBody(text: string | undefined | null): {
  headline: string | null
  body: string
  isStructured: boolean
} {
  if (typeof text !== 'string')
    return { headline: null, body: '', isStructured: false }
  const lines = text.split('\n')
  let headline: string | null = null
  let isStructured = false
  let bodyStartIdx = lines.length
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    // A genuine markdown title: a heading ("# …") or a fully-bolded line
    // ("**…**" / "__…__"). Plain prose first lines don't qualify.
    const isTitle =
      /^#+\s+/.test(line) ||
      /^\*\*.+\*\*$/.test(line) ||
      /^__.+__$/.test(line)
    const cleaned = line
      .replace(/^#+\s*/, '')
      .replace(/^\*+\s*/, '')
      .replace(/\s*\*+$/, '')
      .replace(/[:.\s]+$/, '')
      .trim()
    if (cleaned.length === 0) continue
    isStructured = isTitle
    headline = cleaned.length > 80 ? `${cleaned.slice(0, 77)}…` : cleaned
    bodyStartIdx = i + 1
    // Drop blank lines after the headline so the body doesn't lead with
    // whitespace once the title is removed.
    while (
      bodyStartIdx < lines.length &&
      lines[bodyStartIdx].trim().length === 0
    ) {
      bodyStartIdx++
    }
    break
  }
  return {
    headline,
    body: lines.slice(bodyStartIdx).join('\n'),
    isStructured,
  }
}

type LatestReasoningRowState = {
  show: boolean
  isStreaming: boolean
  headline: string | null
  body: string
}

/** One stable Thinking row — avoids remounting when the reasoning part arrives. */
function resolveLatestReasoningRowState(args: {
  isAssistant: boolean
  isActiveResponse: boolean
  parts: UIMessagePart<UIDataTypes, UITools>[]
  lastReasoningPartIndex: number
  hasActiveStreamIndicator: boolean
}): LatestReasoningRowState | null {
  const {
    isAssistant,
    isActiveResponse,
    parts,
    lastReasoningPartIndex,
    hasActiveStreamIndicator,
  } = args

  if (!isAssistant) return null

  const hasPendingApproval = parts.some(
    (p) => (p as any).state === 'approval-requested'
  )
  const hasApprovalLifecycle = parts.some((p) => {
    const s = (p as any).state
    return (
      s === 'approval-requested' ||
      s === 'approval-responded' ||
      s === 'output-denied' ||
      (p as any).approval != null
    )
  })

  if (lastReasoningPartIndex < 0) {
    if (!isActiveResponse || hasActiveStreamIndicator) return null
    return { show: true, isStreaming: true, headline: null, body: '' }
  }

  const part = parts[lastReasoningPartIndex]
  if (!part || part.type !== 'reasoning') return null

  const reasoningText = (part as { text?: string }).text
  if (
    !reasoningText?.trim() &&
    !isActiveResponse &&
    !hasApprovalLifecycle
  ) {
    return null
  }

  const isReasoningStreaming =
    !hasPendingApproval &&
    ((part as { state?: string }).state === 'streaming' ||
      (isActiveResponse && !reasoningText?.trim()))

  const { headline, body } = splitReasoningBody(reasoningText)

  return {
    show: true,
    isStreaming: isReasoningStreaming,
    headline,
    body,
  }
}

/**
 * Tracks a stable start timestamp for the latest reasoning row so its live
 * "· x.xs" timer and final duration are reliable. The single stable row
 * represents different reasoning blocks over a turn, so the in-component timer
 * alone often misses the streaming→done transition for the latest block. We
 * record the start when streaming begins (carrying the pre-text placeholder's
 * start, key -1, into the first reasoning part, key 0) and reset it when a
 * later reasoning block begins (the key increases). Returns null when no row is
 * shown.
 */
function useReasoningStartTime(
  show: boolean,
  isStreaming: boolean,
  key: number
): number | null {
  const ref = useRef<{ key: number; startedAt: number } | null>(null)
  if (!show) {
    ref.current = null
    return null
  }
  if (isStreaming) {
    const prev = ref.current
    const continues =
      !!prev && (prev.key === key || (prev.key === -1 && key === 0))
    if (!continues) {
      ref.current = { key, startedAt: Date.now() }
    } else if (prev!.key !== key) {
      // Same session (placeholder → first part): keep the original start.
      ref.current = { key, startedAt: prev!.startedAt }
    }
  }
  return ref.current?.startedAt ?? null
}

function getToolDebugInfo(part: any): {
  args: any
  result: any
  isComplete: boolean
  toolName: string
} {
  if (part.state) {
    return {
      toolName: part.type?.startsWith('tool-')
        ? part.type.slice(5)
        : (part.toolName ?? 'unknown'),
      args: part.input ?? {},
      result: part.state === 'output-available' ? part.output : null,
      isComplete: part.state === 'output-available',
    }
  }
  const ti = part.toolInvocation
  if (ti) {
    return {
      toolName: ti.toolName ?? 'unknown',
      args: ti.args ?? {},
      result: ti.state === 'result' ? ti.result : null,
      isComplete: ti.state === 'result',
    }
  }
  return { toolName: 'unknown', args: {}, result: null, isComplete: false }
}

type PartGroup =
  | { kind: 'single'; part: UIMessagePart<UIDataTypes, UITools>; index: number }
  | {
      kind: 'tool-group'
      items: Array<{ part: UIMessagePart<UIDataTypes, UITools>; index: number }>
    }

function groupParts(parts: UIMessagePart<UIDataTypes, UITools>[]): PartGroup[] {
  const groups: PartGroup[] = []
  let currentToolGroup: Array<{
    part: UIMessagePart<UIDataTypes, UITools>
    index: number
  }> = []

  parts.forEach((part, index) => {
    if (isToolPart(part)) {
      currentToolGroup.push({ part, index })
    } else {
      if (currentToolGroup.length > 0) {
        groups.push({ kind: 'tool-group', items: currentToolGroup })
        currentToolGroup = []
      }
      groups.push({ kind: 'single', part, index })
    }
  })

  if (currentToolGroup.length > 0) {
    groups.push({ kind: 'tool-group', items: currentToolGroup })
  }

  return groups
}

// ── Render a single tool part ──────────────────────────────────────────────

export function renderToolPart(
  part: UIMessagePart<UIDataTypes, UITools>,
  messageId: string,
  partIndex: number,
  options?: {
    shouldPollTriggerGithubScan?: boolean
    hasTextContent?: boolean
    isLastAssistantMessage?: boolean
  }
) {
  // PresentOptions / PresentQuestion has its own wrapper style
  if (
    part.type === 'tool-presentOptions' ||
    part.type === 'tool-presentQuestion'
  ) {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <PresentOptionsMessage part={part} />
      </div>
    )
  }
  // Integration tools render interactive buttons inline
  if (part.type === 'tool-connectGithub') {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <ConnectGithubToolMessage part={part} />
      </div>
    )
  }
  if (part.type === 'tool-connectCloud') {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <ConnectCloudToolMessage part={part} />
      </div>
    )
  }
  if (part.type === 'tool-triggerGithubScan') {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <TriggerGithubScanToolMessage
          part={part}
          shouldPoll={options?.shouldPollTriggerGithubScan ?? false}
        />
      </div>
    )
  }
  if (part.type === 'tool-searchKnowledge') {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <SearchKnowledgeMessage part={part} />
      </div>
    )
  }
  if (part.type === 'tool-policiesProposeApplyBlocks') {
    return (
      <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
        <ProposeApplyBlocksMessage part={part} />
      </div>
    )
  }

  // Human-in-the-loop: a tool flagged `needsApproval` on the server (driven
  // by `audit.requiresHuman`) pauses here as an `approval-requested` part.
  // ApprovalRequestCard renders the Approve/Cancel UI; chat.tsx feeds the
  // choice back into the SDK to resume or abort the call.
  //
  // We keep the card mounted through the entire approval lifecycle —
  // `approval-requested` → `approval-responded` → `output-available` /
  // `output-denied` / `output-error` — so the user always has a visual
  // record of their decision. For resolved approvals (output-available /
  // output-error) we render the card AND fall through to the per-tool
  // message renderer below; that path runs the per-tool
  // `useInvalidateEvidenceSubmissionQueries` hook that refreshes the
  // submissions panel cache and shows the result detail.
  const partState = (part as any).state
  const isPendingApprovalPart =
    partState === 'approval-requested' ||
    partState === 'approval-responded' ||
    partState === 'output-denied'
  if (isPendingApprovalPart) {
    return <ApprovalRequestCard key={`${messageId}-${partIndex}`} part={part} />
  }
  const isResolvedApprovalPart =
    (part as any).approval != null &&
    (partState === 'output-available' || partState === 'output-error')

  const debug = getToolDebugInfo(part)
  const renderUnhandledTool = (toolName: string) => (
    <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
      <ToolStatusLine
        state={readToolState(part)}
        loadingLabel='Running tool'
        completedLabel='Tool completed'
        loadingDetail={toolName}
        completedDetail={toolName}
        completedIcon={
          <Wrench className='h-3.5 w-3.5 shrink-0 text-emerald-500' />
        }
      />
      <ToolDebugPanel
        toolName={toolName}
        args={debug.args}
        result={debug.result}
        isComplete={debug.isComplete}
      />
    </div>
  )

  // v6 tool parts
  if (part.type.startsWith('tool-') && part.type !== 'tool-invocation') {
    // tool-input-error parts use toolName property; normal tool parts use type prefix
    const toolName =
      part.type === 'tool-input-error'
        ? ((part as any).toolName ?? 'unknown')
        : part.type.slice(5)
    const ToolComponent =
      TOOL_MESSAGE_COMPONENTS[toolName as keyof typeof TOOL_MESSAGE_COMPONENTS]
    if (ToolComponent) {
      const shouldPoll =
        toolName === 'triggerGithubScan'
          ? (options?.shouldPollTriggerGithubScan ?? false)
          : undefined
      return (
        <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
          {isResolvedApprovalPart && (
            <ApprovalRequestCard
              key={`${messageId}-${partIndex}-approval`}
              part={part}
            />
          )}
          <ToolComponent
            part={part}
            icon={
              TOOL_MESSAGE_ICONS[toolName as keyof typeof TOOL_MESSAGE_ICONS]
            }
            shouldPoll={shouldPoll}
            isLastAssistantMessage={options?.isLastAssistantMessage}
          />
          <IntegrationConnectMessage part={part} />
          <ToolDebugPanel
            toolName={toolName}
            args={debug.args}
            result={debug.result}
            isComplete={debug.isComplete}
          />
        </div>
      )
    }
    return renderUnhandledTool(toolName)
  }

  // Legacy tool invocations
  if (part.type === 'tool-invocation') {
    const legacyToolName = (part as any).toolInvocation.toolName
    const ToolComponent =
      TOOL_MESSAGE_COMPONENTS[
        legacyToolName as keyof typeof TOOL_MESSAGE_COMPONENTS
      ]
    if (ToolComponent) {
      const shouldPoll =
        legacyToolName === 'triggerGithubScan'
          ? (options?.shouldPollTriggerGithubScan ?? false)
          : undefined
      return (
        <div key={`${messageId}-${partIndex}`} className='mb-2.5 last:mb-0'>
          <ToolComponent
            part={part}
            icon={
              TOOL_MESSAGE_ICONS[
                legacyToolName as keyof typeof TOOL_MESSAGE_ICONS
              ]
            }
            shouldPoll={shouldPoll}
            isLastAssistantMessage={options?.isLastAssistantMessage}
          />
          <IntegrationConnectMessage part={part} />
          <ToolDebugPanel
            toolName={legacyToolName}
            args={debug.args}
            result={debug.result}
            isComplete={debug.isComplete}
          />
        </div>
      )
    }
    if (import.meta.env.DEV) {
      return renderUnhandledTool((part as any).toolInvocation.toolName)
    }
    return renderUnhandledTool((part as any).toolInvocation.toolName)
  }

  return null
}

// ── Pipeline status for policy-generate scope ─────────────────────────────

type PipelineStatusEvent = {
  kind: 'status' | 'done'
  stage: string
  state?: 'start' | 'end'
  label: string
  detail?: string
  sections?: string[]
  findings?: Array<{ severity?: string; sectionKey?: string; message?: string }>
  claimsRecorded?: number
  reviewStatus?: string | null
}

const SECTION_LABELS: Record<string, string> = {
  purpose: 'Purpose',
  scope: 'Scope',
  policyStatements: 'Policy Statements',
  rolesResponsibilities: 'Roles & Responsibilities',
  exceptions: 'Exceptions',
  enforcement: 'Enforcement',
}

function humanizeSectionKey(key: string): string {
  if (SECTION_LABELS[key]) return SECTION_LABELS[key]
  return key
    .replace(/[_-]/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

const PIPELINE_STAGE_ICON: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  writing: FilePenLine,
  saved: FileText,
  claims: Shield,
  reclaims: Shield,
  review: FileSearch,
  revise: Wrench,
  done: Sparkles,
}

function iconForStage(
  stage: string
): React.ComponentType<{ className?: string }> {
  return PIPELINE_STAGE_ICON[stage] ?? ClipboardCheck
}

function activeLabelForStage(stage: string, serverLabel: string): string {
  switch (stage) {
    case 'writing':
      return 'Writing policy draft'
    case 'saved':
      return 'Saving policy draft'
    case 'claims':
      return 'Recording claims'
    case 'reclaims':
      return 'Re-recording claims'
    case 'review':
      return 'Running compliance review'
    case 'revise':
      return serverLabel // "Fixing N section(s)" — server knows the count
    default:
      return serverLabel
  }
}

function completedLabelForStage(stage: string, serverLabel: string): string {
  // When the server already sends a past-tense label on its "end" event, pass it through — it knows outcomes (e.g. "Compliance review passed" vs "flagged issues", actual section counts).
  switch (stage) {
    case 'writing':
    case 'saved':
      return 'Policy draft saved'
    default:
      return serverLabel
  }
}

function activeDetailForStage(
  stage: string,
  serverDetail: string | undefined
): string | undefined {
  return serverDetail
}

function completedDetailForStage(
  stage: string,
  serverDetail: string | undefined,
  _sections: string[] | undefined
): string | undefined {
  // Server "end" events already ship past-tense detail strings with real counts — trust them. Only override for stages without a dedicated "end" event.
  if (stage === 'saved')
    return 'Draft persisted; running compliance checks next.'
  return serverDetail
}

function usePolicyDraftLiveStream(
  parts: UIMessagePart<UIDataTypes, UITools>[],
  isStreaming: boolean
) {
  // The first text part is the policy markdown being drafted. While the chat is streaming, dispatch deltas so the policy editor panel renders the draft live (see usePolicyDraftStreamEvent). Once streaming ends, stop dispatching — handleStreamFinish clears liveDraftContent and the editor swaps back to the server-saved TipTap doc that carries claim docBlockIds, so pills render. Dispatching a final snapshot here would re-flip isGeneratingPolicy back to true and keep the unannotated live preview on screen.
  const prevTextRef = useRef('')
  const lastStageKeyRef = useRef('')

  useEffect(() => {
    if (!isStreaming) return
    const firstTextPart = parts.find((p) => p.type === 'text') as
      | { text?: string }
      | undefined
    const current = firstTextPart?.text ?? ''
    if (!current) return
    const prev = prevTextRef.current
    if (current.length > prev.length && current.startsWith(prev)) {
      const delta = current.slice(prev.length)
      prevTextRef.current = current
      if (delta) {
        console.log('[usePolicyDraftLiveStream] dispatching delta', {
          deltaLen: delta.length,
          totalLen: current.length,
        })
        window.dispatchEvent(
          new CustomEvent('screenata:policy-draft-stream', {
            detail: { policyContentDelta: delta },
          })
        )
      }
    } else if (current !== prev) {
      prevTextRef.current = current
      console.log('[usePolicyDraftLiveStream] dispatching full content', {
        contentLen: current.length,
      })
      window.dispatchEvent(
        new CustomEvent('screenata:policy-draft-stream', {
          detail: { policyContent: current },
        })
      )
    }
  }, [parts, isStreaming])

  // Mirror the latest pipeline-status part out to the editor panel as a `screenata:policy-stage` event so the in-editor banner can display the current stage label/detail ("Recording claims", "Compliance review complete", …).
  useEffect(() => {
    const pipelineParts = parts.filter(
      (part) => (part as any).type === 'data-policy-pipeline-status'
    )
    const latest = pipelineParts[pipelineParts.length - 1] as any
    const data = latest?.data
    if (!data || data.kind !== 'status' || !data.label) return
    const key = `${data.stage}:${data.state ?? ''}:${data.label}:${data.detail ?? ''}`
    if (lastStageKeyRef.current === key) return
    lastStageKeyRef.current = key
    window.dispatchEvent(
      new CustomEvent('screenata:policy-stage', {
        detail: {
          stage: data.stage,
          state: data.state,
          label: data.label,
          detail: data.detail,
        },
      })
    )
  }, [parts])

  useEffect(() => {
    if (isStreaming) return
    console.log(
      '[usePolicyDraftLiveStream] stream ended → dispatching policy-stage clear'
    )
    window.dispatchEvent(
      new CustomEvent('screenata:policy-stage', { detail: { clear: true } })
    )
  }, [isStreaming])
}

export type PolicyPipelineEvent = {
  stage: string
  state?: 'start' | 'end'
  label: string
  detail?: string
  sections?: string[]
  findings?: Array<{ severity?: string; sectionKey?: string; message?: string }>
}

export function PolicyPipelineTimeline({
  events,
  isStreaming,
  isAborted,
  doneSummary,
}: {
  events: PolicyPipelineEvent[]
  isStreaming: boolean
  isAborted: boolean
  doneSummary?: {
    claimsRecorded?: number
    reviewStatus?: string | null
  } | null
}) {
  // Collapse per stage — end events override starts except for `revise`, where the end event intentionally drops the start's findings/detail (already shown under the review row).
  const timeline: PolicyPipelineEvent[] = []
  const stageIndex = new Map<string, number>()
  for (const event of events) {
    const existingIdx = stageIndex.get(event.stage)
    if (existingIdx === undefined) {
      stageIndex.set(event.stage, timeline.length)
      timeline.push(event)
      continue
    }
    const existing = timeline[existingIdx]
    const isReviseEnd = existing.stage === 'revise' && event.state === 'end'
    if (isReviseEnd) {
      timeline[existingIdx] = {
        ...event,
        detail: undefined,
        findings: undefined,
        sections: event.sections ?? existing.sections,
      }
      continue
    }
    timeline[existingIdx] = {
      ...existing,
      ...event,
      sections: event.sections ?? existing.sections,
      findings: event.findings ?? existing.findings,
    }
  }

  type PipelineRow = {
    key: string
    stage: string
    label: string
    detail?: string
    sections?: string[]
    findings?: Array<{
      severity?: string
      sectionKey?: string
      message?: string
    }>
    state: 'done' | 'active' | 'cancelled'
  }
  const rows: PipelineRow[] = []
  const hasPipelineStarted = timeline.length > 0

  if (isStreaming && !hasPipelineStarted) {
    rows.push({
      key: 'writing',
      stage: 'writing',
      label: activeLabelForStage('writing', 'Writing policy draft'),
      state: 'active',
    })
  } else if (isAborted && !hasPipelineStarted) {
    rows.push({
      key: 'writing',
      stage: 'writing',
      label: 'Writing policy draft',
      state: 'cancelled',
    })
  }

  timeline.forEach((event, idx) => {
    const isLast = idx === timeline.length - 1
    let state: 'done' | 'active' | 'cancelled'
    if (event.state === 'end') {
      state = 'done'
    } else if (isLast && !doneSummary) {
      if (isAborted) state = 'cancelled'
      else if (isStreaming) state = 'active'
      else state = 'done'
    } else {
      state = 'done'
    }
    rows.push({
      key: `${event.stage}-${idx}`,
      stage: event.stage,
      label:
        state === 'active' || state === 'cancelled'
          ? activeLabelForStage(event.stage, event.label)
          : completedLabelForStage(event.stage, event.label),
      detail:
        state === 'active'
          ? activeDetailForStage(event.stage, event.detail)
          : state === 'cancelled'
            ? undefined
            : completedDetailForStage(
                event.stage,
                event.detail,
                event.sections
              ),
      sections: state === 'cancelled' ? undefined : event.sections,
      findings: state === 'cancelled' ? undefined : event.findings,
      state,
    })
  })

  if (doneSummary) {
    const claims = doneSummary.claimsRecorded ?? 0
    const reviewLabel =
      doneSummary.reviewStatus === 'PASS'
        ? 'review passed'
        : doneSummary.reviewStatus === 'NEEDS_REWRITE'
          ? 'review flagged issues'
          : 'review complete'
    rows.push({
      key: 'done',
      stage: 'done',
      label: 'Policy ready',
      detail: `${claims} claim${claims !== 1 ? 's' : ''} recorded · ${reviewLabel} · draft saved to editor.`,
      state: 'done',
    })
  }

  if (isAborted) {
    rows.push({
      key: 'aborted',
      stage: 'aborted',
      label: 'Request cancelled',
      detail:
        'You stopped this generation before it finished. Any partial draft saved so far is still in the editor.',
      state: 'cancelled',
    })
  }

  return (
    <div className='flex flex-col'>
      {rows.map((row) => {
        const isActive = row.state === 'active'
        const isCancelled = row.state === 'cancelled'
        const isDoneSummary = row.stage === 'done'
        const Icon = iconForStage(row.stage)
        return (
          <div key={row.key} className='flex items-start gap-2 py-1 text-xs'>
            {isActive ? (
              <Loader2 className='text-muted-foreground mt-[2px] h-3.5 w-3.5 shrink-0 animate-spin' />
            ) : isCancelled ? (
              <CircleAlert className='mt-[2px] h-3.5 w-3.5 shrink-0 text-status-orange' />
            ) : (
              <Icon
                className={cn(
                  'mt-[2px] h-3.5 w-3.5 shrink-0',
                  isDoneSummary
                    ? 'text-status-green'
                    : 'text-muted-foreground/70'
                )}
              />
            )}
            <div className='flex min-w-0 flex-col leading-[18px]'>
              <span className='text-foreground/80 font-medium'>
                {row.label}
              </span>
              {row.detail ? (
                <span className='text-muted-foreground text-[11px]'>
                  {row.detail}
                </span>
              ) : null}
              {row.findings && row.findings.length > 0 ? (
                <ul className='text-muted-foreground mt-1 space-y-1 text-[11px]'>
                  {row.findings.map((f, i) => {
                    const isBlocker = f.severity === 'blocker'
                    return (
                      <li key={i} className='flex gap-1.5'>
                        <span
                          className={cn(
                            'mt-[5px] inline-block h-1 w-1 shrink-0 rounded-full',
                            isBlocker
                              ? 'bg-status-orange'
                              : 'bg-muted-foreground/40'
                          )}
                        />
                        <span className='min-w-0'>
                          {f.sectionKey ? (
                            <span className='text-foreground/70 font-medium'>
                              {humanizeSectionKey(f.sectionKey)}
                            </span>
                          ) : null}
                          {f.severity ? (
                            <span
                              className={cn(
                                'ml-1 rounded-sm px-1 py-px text-[9px] tracking-wide uppercase',
                                isBlocker
                                  ? 'bg-status-orange-bg text-status-orange-strong'
                                  : 'bg-muted text-muted-foreground'
                              )}
                            >
                              {f.severity}
                            </span>
                          ) : null}
                          {f.message ? (
                            <span className='ml-1'>{f.message}</span>
                          ) : null}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              ) : row.sections && row.sections.length > 0 ? (
                <ul className='text-muted-foreground mt-1 space-y-0.5 text-[11px]'>
                  {row.sections.map((s) => (
                    <li key={s} className='flex gap-1.5'>
                      <span className='text-muted-foreground/60'>•</span>
                      <span>
                        {row.stage === 'revise' ? (
                          <>
                            <span className='text-muted-foreground'>
                              {row.state === 'active'
                                ? 'Updating section: '
                                : 'Updated section: '}
                            </span>
                            <span className='text-foreground/70 font-medium'>
                              {humanizeSectionKey(s)}
                            </span>
                          </>
                        ) : (
                          <span className='text-foreground/70'>
                            {humanizeSectionKey(s)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PolicyGeneratePipelineStatus({
  messageId,
  parts,
  isStreaming,
  isAborted,
}: {
  messageId: string
  parts: UIMessagePart<UIDataTypes, UITools>[]
  isStreaming: boolean
  isAborted: boolean
}) {
  usePolicyDraftLiveStream(parts, isStreaming)
  const pipelineEvents = useMemo(() => {
    const events: PipelineStatusEvent[] = []
    for (const part of parts) {
      if ((part as any).type !== 'data-policy-pipeline-status') continue
      const data = (part as any).data
      if (!data || typeof data !== 'object') continue
      events.push(data as PipelineStatusEvent)
    }
    return events
  }, [parts])

  const statusEvents = pipelineEvents.filter((e) => e.kind === 'status')
  const doneEvent = pipelineEvents.find((e) => e.kind === 'done')
  const hasPipelineStarted = statusEvents.length > 0

  // Collapse per stage. An "end" event for a stage overrides its "start"; we carry over sections/findings from either event so no data is lost — except for the revise stage, where the end row only needs the section keys (the blocker findings already appeared under the review row).
  const timeline: PipelineStatusEvent[] = []
  const stageIndex = new Map<string, number>()
  for (const event of statusEvents) {
    const existingIdx = stageIndex.get(event.stage)
    if (existingIdx === undefined) {
      stageIndex.set(event.stage, timeline.length)
      timeline.push(event)
      continue
    }
    const existing = timeline[existingIdx]
    const isReviseEnd = existing.stage === 'revise' && event.state === 'end'
    if (isReviseEnd) {
      // Don't carry the start event's "Applying rewrites for N blocking finding(s)" / findings into the done row — blockers already appeared under the compliance-review row, and the end event only needs the applied section keys.
      timeline[existingIdx] = {
        ...event,
        detail: undefined,
        findings: undefined,
        sections: event.sections ?? existing.sections,
      }
      continue
    }
    timeline[existingIdx] = {
      ...existing,
      ...event,
      sections: event.sections ?? existing.sections,
      findings: event.findings ?? existing.findings,
    }
  }

  type PipelineRow = {
    key: string
    stage: string
    label: string
    detail?: string
    sections?: string[]
    findings?: Array<{
      severity?: string
      sectionKey?: string
      message?: string
    }>
    state: 'done' | 'active' | 'cancelled'
  }
  const rows: PipelineRow[] = []

  // Transient: only show while the text is streaming and no pipeline event has arrived.
  if (isStreaming && !hasPipelineStarted) {
    rows.push({
      key: 'writing',
      stage: 'writing',
      label: activeLabelForStage('writing', 'Writing policy draft'),
      state: 'active',
    })
  } else if (isAborted && !hasPipelineStarted) {
    rows.push({
      key: 'writing',
      stage: 'writing',
      label: 'Writing policy draft',
      state: 'cancelled',
    })
  }

  timeline.forEach((event, idx) => {
    const isLast = idx === timeline.length - 1
    // An explicit `state === 'end'` from the server marks a stage as complete. A trailing "start" event becomes "cancelled" when the chat was aborted mid-stream, "active" while streaming, and "done" otherwise (i.e. the next stage's start arrived).
    let state: 'done' | 'active' | 'cancelled'
    if (event.state === 'end') {
      state = 'done'
    } else if (isLast && !doneEvent) {
      if (isAborted) state = 'cancelled'
      else if (isStreaming) state = 'active'
      else state = 'done'
    } else {
      state = 'done'
    }
    rows.push({
      key: `${event.stage}-${idx}`,
      stage: event.stage,
      label:
        state === 'active' || state === 'cancelled'
          ? activeLabelForStage(event.stage, event.label)
          : completedLabelForStage(event.stage, event.label),
      detail:
        state === 'active'
          ? activeDetailForStage(event.stage, event.detail)
          : state === 'cancelled'
            ? undefined
            : completedDetailForStage(
                event.stage,
                event.detail,
                event.sections
              ),
      sections: state === 'cancelled' ? undefined : event.sections,
      findings: state === 'cancelled' ? undefined : event.findings,
      state,
    })
  })

  if (isAborted) {
    rows.push({
      key: 'aborted',
      stage: 'aborted',
      label: 'Request cancelled',
      detail:
        'You stopped this generation before it finished. Any partial draft saved so far is still in the editor.',
      state: 'cancelled',
    })
  }

  if (doneEvent) {
    const claims = doneEvent.claimsRecorded ?? 0
    const reviewLabel =
      doneEvent.reviewStatus === 'PASS'
        ? 'review passed'
        : doneEvent.reviewStatus === 'NEEDS_REWRITE'
          ? 'review flagged issues'
          : 'review complete'
    rows.push({
      key: 'done',
      stage: 'done',
      label: 'Policy ready',
      detail: `${claims} claim${claims !== 1 ? 's' : ''} recorded · ${reviewLabel} · draft saved to editor.`,
      state: 'done',
    })
  }

  return (
    <div key={`${messageId}-pipeline`} className='flex flex-col'>
      {rows.map((row) => {
        const isActive = row.state === 'active'
        const isCancelled = row.state === 'cancelled'
        const isDoneSummary = row.stage === 'done'
        const Icon = iconForStage(row.stage)
        return (
          <div key={row.key} className='flex items-start gap-2 py-1 text-xs'>
            {isActive ? (
              <Loader2 className='text-muted-foreground mt-[2px] h-3.5 w-3.5 shrink-0 animate-spin' />
            ) : isCancelled ? (
              <CircleAlert className='mt-[2px] h-3.5 w-3.5 shrink-0 text-status-orange' />
            ) : (
              <Icon
                className={cn(
                  'mt-[2px] h-3.5 w-3.5 shrink-0',
                  isDoneSummary
                    ? 'text-status-green'
                    : 'text-muted-foreground/70'
                )}
              />
            )}
            <div className='flex min-w-0 flex-col leading-[18px]'>
              <span className='text-foreground/80 font-medium'>
                {row.label}
              </span>
              {row.detail ? (
                <span className='text-muted-foreground text-[11px]'>
                  {row.detail}
                </span>
              ) : null}
              {row.findings && row.findings.length > 0 ? (
                <ul className='text-muted-foreground mt-1 space-y-1 text-[11px]'>
                  {row.findings.map((f, i) => {
                    const isBlocker = f.severity === 'blocker'
                    return (
                      <li key={i} className='flex gap-1.5'>
                        <span
                          className={cn(
                            'mt-[5px] inline-block h-1 w-1 shrink-0 rounded-full',
                            isBlocker
                              ? 'bg-status-orange'
                              : 'bg-muted-foreground/40'
                          )}
                        />
                        <span className='min-w-0'>
                          {f.sectionKey ? (
                            <span className='text-foreground/70 font-medium'>
                              {humanizeSectionKey(f.sectionKey)}
                            </span>
                          ) : null}
                          {f.severity ? (
                            <span
                              className={cn(
                                'ml-1 rounded-sm px-1 py-px text-[9px] tracking-wide uppercase',
                                isBlocker
                                  ? 'bg-status-orange-bg text-status-orange-strong'
                                  : 'bg-muted text-muted-foreground'
                              )}
                            >
                              {f.severity}
                            </span>
                          ) : null}
                          {f.message ? (
                            <span className='ml-1'>{f.message}</span>
                          ) : null}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              ) : row.sections && row.sections.length > 0 ? (
                <ul className='text-muted-foreground mt-1 space-y-0.5 text-[11px]'>
                  {row.sections.map((s) => (
                    <li key={s} className='flex gap-1.5'>
                      <span className='text-muted-foreground/60'>•</span>
                      <span>
                        {row.stage === 'revise' ? (
                          <>
                            <span className='text-muted-foreground'>
                              {row.state === 'active'
                                ? 'Updating section: '
                                : 'Updated section: '}
                            </span>
                            <span className='text-foreground/70 font-medium'>
                              {humanizeSectionKey(s)}
                            </span>
                          </>
                        ) : (
                          <span className='text-foreground/70'>
                            {humanizeSectionKey(s)}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const ChatMessageItem = memo(function ChatMessageItem({
  message,
  isLastAssistant,
  isLatestTriggerGithubScanMessage,
  isReady,
  isStreamingMessage,
  isActiveResponse,
  isFirstInGroup,
  sources,
  chatEventScope,
  onRegenerate,
}: ChatMessageItemProps) {
  const isAssistant = message.role === 'assistant'
  const isErrorMessage = (message.metadata as any)?.isError === true
  const parts = message.parts ?? []
  const [previewFile, setPreviewFile] =
    useState<ChatAttachmentPreviewFile | null>(null)
  const userAttachments = !isAssistant
    ? (message.metadata as any)?.uiAttachments
    : undefined
  const normalizedUserAttachments = useMemo(
    () =>
      Array.isArray(userAttachments)
        ? userAttachments.filter(
            (attachment): attachment is ChatAttachmentPreviewFile =>
              typeof attachment?.url === 'string' &&
              typeof attachment?.name === 'string'
          )
        : [],
    [userAttachments]
  )
  // Attached vault docs / policies (no preview url — rendered as static pills,
  // mirroring the composer's attachment row). Written to message metadata at
  // send time alongside uiAttachments.
  const userDocAttachments = !isAssistant
    ? (message.metadata as any)?.uiDocumentAttachments
    : undefined
  const normalizedUserDocAttachments: Array<{
    documentType: string
    documentId: string
    title: string
  }> = useMemo(
    () =>
      Array.isArray(userDocAttachments)
        ? userDocAttachments.filter(
            (doc: any) =>
              typeof doc?.title === 'string' &&
              typeof doc?.documentId === 'string'
          )
        : [],
    [userDocAttachments]
  )
  const userImageAttachments = normalizedUserAttachments.filter(
    (attachment) =>
      getChatAttachmentPresentation(attachment.type, attachment.name).kind ===
      'image'
  )
  const userFileAttachments = normalizedUserAttachments.filter(
    (attachment) =>
      getChatAttachmentPresentation(attachment.type, attachment.name).kind !==
      'image'
  )
  const groups = useMemo(
    () => (isAssistant ? groupParts(parts) : null),
    [isAssistant, parts]
  )
  // The dedicated policy-chat endpoint is identified by scope OR by the presence of data-policy-pipeline-status parts. Metadata.chatKind is NOT enough because the screenata-chat flow (tool-based generatePolicy) also stamps that metadata, and in that flow there are no data-pipeline-status parts — so this component would render a phantom pipeline timeline alongside the GeneratePolicyMessage tool row.
  const isPolicyGenerateMessage = useMemo(() => {
    if (!isAssistant) return false
    if (chatEventScope === 'screenata-policy-generate') return true
    return parts.some(
      (part) => (part as any).type === 'data-policy-pipeline-status'
    )
  }, [isAssistant, chatEventScope, parts])
  const allTextContent = useMemo(() => {
    if (!isAssistant) return ''

    return parts
      .filter((part) => part.type === 'text')
      .map((part) => (part as any).text)
      .join('\n\n')
  }, [isAssistant, parts])
  const visibleTextContent = useMemo(
    () => allTextContent.replace(/\s+/g, ' ').trim(),
    [allTextContent]
  )
  const hasRenderableContent = useMemo(() => {
    if (!isAssistant) return true
    if (isActiveResponse) return true

    return parts.some((part) => {
      if (part.type === 'reasoning') {
        // Reasoning parts are renderable during streaming (show "Thinking..." indicator)
        // even before text content arrives
        return isStreamingMessage || Boolean((part as any).text?.trim())
      }
      if (part.type === 'text') {
        return Boolean((part as any).text?.trim())
      }

      return isToolPart(part)
    })
  }, [isAssistant, parts, isStreamingMessage, isActiveResponse])
  const lastReasoningPartIndex = useMemo(() => {
    if (!isAssistant) return -1

    for (let i = parts.length - 1; i >= 0; i--) {
      if (parts[i]?.type === 'reasoning') return i
    }

    return -1
  }, [isAssistant, parts])
  const hasActiveStreamIndicator = isAssistant
    ? assistantMessageHasActiveStreamIndicator(message)
    : false
  const latestReasoningRow = useMemo(
    () =>
      resolveLatestReasoningRowState({
        isAssistant,
        isActiveResponse,
        parts,
        lastReasoningPartIndex,
        hasActiveStreamIndicator,
      }),
    [
      isAssistant,
      isActiveResponse,
      parts,
      lastReasoningPartIndex,
      hasActiveStreamIndicator,
    ]
  )
  const reasoningStartTime = useReasoningStartTime(
    latestReasoningRow?.show ?? false,
    latestReasoningRow?.isStreaming ?? false,
    lastReasoningPartIndex
  )

  // Trailing "Thinking …" row for the idle gaps WITHIN an active turn that the
  // indicators above don't cover: after a reasoning block or tool completes and
  // the model is computing its next step, no part is streaming yet, so neither
  // the streaming-reasoning shimmer nor an in-flight tool row is visible. The
  // top pre-reasoning placeholder only handles the gap before the first
  // reasoning part exists (lastReasoningPartIndex < 0), leaving every
  // subsequent gap (reason → tool → reason …) with no "working" signal.
  const lastPart = parts.length > 0 ? parts[parts.length - 1] : undefined
  const lastPartIsStreamingText =
    lastPart?.type === 'text' &&
    Boolean((lastPart as { text?: string }).text?.trim())
  const hasPendingApproval = parts.some(
    (p) => (p as { state?: string }).state === 'approval-requested'
  )
  const topPlaceholderShown =
    Boolean(latestReasoningRow?.show) && lastReasoningPartIndex < 0
  const showTrailingThinkingRow =
    isAssistant &&
    isActiveResponse &&
    parts.length > 0 &&
    !hasActiveStreamIndicator &&
    !lastPartIsStreamingText &&
    !hasPendingApproval &&
    !topPlaceholderShown

  // The chat has stopped when the status is ready and this message is not
  // actively streaming.  Any tool part still showing a spinner is stale.
  const chatStopped = isReady && !isStreamingMessage
  // Policy-generate pipeline considered aborted when the chat stopped before the server emitted its final `done` data event.
  const isPolicyPipelineAborted = useMemo(() => {
    if (!isPolicyGenerateMessage) return false
    if (!chatStopped) return false
    return !parts.some(
      (part) =>
        (part as any).type === 'data-policy-pipeline-status' &&
        (part as any).data?.kind === 'done'
    )
  }, [isPolicyGenerateMessage, chatStopped, parts])

  return (
    <Message key={message.id} from={message.role}>
      <ChatStoppedContext.Provider value={chatStopped}>
        <div
          className={cn(
            'group/assistant flex flex-col',
            isAssistant ? 'w-full' : 'w-full items-end'
          )}
        >
          {!isAssistant && userImageAttachments.length > 0 && (
            <div className='mb-1.5 flex flex-wrap justify-end gap-1.5'>
              {userImageAttachments.map((attachment, index) => (
                <button
                  key={attachment.id ?? `${message.id}-attachment-${index}`}
                  type='button'
                  className='h-10 w-10 overflow-hidden rounded-md border border-border bg-card p-0 transition-opacity hover:opacity-85'
                  onClick={() => setPreviewFile(attachment)}
                >
                  <img
                    src={attachment.url}
                    alt=''
                    className='h-full w-full object-cover'
                  />
                </button>
              ))}
            </div>
          )}
          {!isAssistant && userFileAttachments.length > 0 && (
            <div className='mb-1.5 flex max-w-[min(520px,100%)] flex-wrap justify-end gap-1.5'>
              {userFileAttachments.map((attachment, index) => (
                <button
                  key={attachment.url ?? `${message.id}-file-${index}`}
                  type='button'
                  aria-label={`Preview ${attachment.name}`}
                  className='max-w-full appearance-none border-0 bg-transparent p-0 text-left'
                  onClick={() => setPreviewFile(attachment)}
                >
                  <UserMessageAttachmentPill attachment={attachment} />
                </button>
              ))}
            </div>
          )}
          {!isAssistant && normalizedUserDocAttachments.length > 0 && (
            <div className='mb-1.5 flex max-w-[min(520px,100%)] flex-wrap justify-end gap-1.5'>
              {normalizedUserDocAttachments.map((doc) => (
                <AttachmentPill
                  key={`${doc.documentType}:${doc.documentId}`}
                  icon={<FileText className='text-muted-foreground h-3.5 w-3.5' />}
                  name={doc.title}
                  label={doc.documentType === 'policy' ? 'Policy' : 'Doc'}
                />
              ))}
            </div>
          )}
          {isAssistant && !hasRenderableContent ? null : (
            <MessageContent className='group/msg group-[.is-assistant]:w-full group-[.is-assistant]:gap-4 group-[.is-assistant]:rounded-none group-[.is-assistant]:border-none group-[.is-assistant]:bg-transparent group-[.is-assistant]:p-0 group-[.is-user]:rounded-[18px_4px_18px_18px]'>
              {/* Pre-reasoning "Thinking …" placeholder ONLY — shown while the
                  assistant turn is active but no reasoning part has streamed in
                  yet (lastReasoningPartIndex < 0). Once reasoning parts exist
                  they render inline below, in streaming order, so the latest
                  reasoning is never pinned out-of-sequence above earlier rows. */}
              {latestReasoningRow?.show && lastReasoningPartIndex < 0 ? (
                <Reasoning
                  key={`${message.id}-reasoning-row`}
                  className='mb-0 w-full [overflow-anchor:none]'
                  isStreaming={latestReasoningRow.isStreaming}
                  startTime={reasoningStartTime}
                >
                  <ReasoningTrigger summary={null} expandable={false} />
                </Reasoning>
              ) : null}
              {isAssistant && groups
                ? groups.map((group, groupIndex) => {
                    if (group.kind === 'single') {
                      const { part, index: partIndex } = group

                      if (part.type === 'text') {
                        const text = (part as { text?: string }).text
                        // Policy-generate: the FIRST text part is the policy markdown streamed to the editor — replace it with the pipeline status. Subsequent text parts are Vera's summary/prompt to review and render as normal chat text.
                        if (isPolicyGenerateMessage) {
                          const firstTextIndex = parts.findIndex(
                            (p) => p.type === 'text'
                          )
                          if (partIndex === firstTextIndex) {
                            return (
                              <PolicyGeneratePipelineStatus
                                key={`${message.id}-${partIndex}`}
                                messageId={message.id}
                                parts={parts}
                                isStreaming={isStreamingMessage}
                                isAborted={isPolicyPipelineAborted}
                              />
                            )
                          }
                          // Fall through to the normal text rendering for the summary.
                        }

                        if (!text?.trim()) return null

                        if (isLastAssistant && chatEventScope && isReady) {
                          return (
                            <ApplyableResponse
                              key={`${message.id}-${partIndex}`}
                              content={text}
                              sources={sources}
                            />
                          )
                        }

                        return (
                          <ResponseWithCitations
                            key={`${message.id}-${partIndex}`}
                            content={text}
                            sources={sources}
                          />
                        )
                      }

                      if (part.type === 'reasoning') {
                        const reasoningText = (part as any).text
                        const hasPendingApproval = parts.some(
                          (p) => (p as any).state === 'approval-requested'
                        )
                        // Any part that went through an approval flow —
                        // pending, denied, OR resolved. Used to preserve
                        // the reasoning placeholder that was visible
                        // while the user was deciding, even after the
                        // tool resolves and message streaming ends.
                        const hasApprovalLifecycle = parts.some((p) => {
                          const s = (p as any).state
                          return (
                            s === 'approval-requested' ||
                            s === 'approval-responded' ||
                            s === 'output-denied' ||
                            (p as any).approval != null
                          )
                        })
                        const isLatestReasoningPart =
                          partIndex === lastReasoningPartIndex
                        // Use the AI SDK's own `state` field on the
                        // reasoning part as the streaming signal. The
                        // earlier derivation looked at message-level
                        // streaming + `partIndex === parts.length - 1`,
                        // which breaks the moment a text part appears
                        // after the reasoning part (the reasoning is no
                        // longer last). On `reasoningSummary: 'concise'`
                        // the summary often lands in one chunk and the
                        // text part starts in the same render — so the
                        // indirect signal never observed `isStreaming:
                        // true` and the timer never ran, leaving the
                        // fallback "Thought for a few seconds" label
                        // every turn.
                        const isReasoningStreaming =
                          !hasPendingApproval &&
                          ((part as any).state === 'streaming' ||
                            (isStreamingMessage &&
                              isLatestReasoningPart &&
                              !reasoningText?.trim()))
                        // Keep reasoning visible across the entire
                        // approval lifecycle even if text is empty so
                        // the placeholder shown to the user while they
                        // were deciding doesn't vanish after the tool
                        // resolves.
                        // Cull only non-latest empty reasoning parts. The
                        // latest reasoning part is kept even when it carries
                        // no text and streaming has ended, so the
                        // "Thinking …" → "Thought for …" label stays in
                        // place instead of vanishing and shifting the layout
                        // below it. It renders non-expandable when there's
                        // no body to reveal.
                        if (
                          !reasoningText?.trim() &&
                          !isLatestReasoningPart &&
                          !hasApprovalLifecycle
                        )
                          return null
                        const reasoningFull = (reasoningText ?? '').trim()
                        const {
                          headline: reasoningHeadline,
                          body: reasoningBody,
                          isStructured,
                        } = splitReasoningBody(reasoningText)
                        const headlineTruncated =
                          reasoningHeadline?.endsWith('…') ?? false
                        // Only strip the first line into the body when it's a
                        // genuine markdown title (bold/heading) — then the
                        // trigger owns the title and the body shows the rest.
                        // For plain-prose first lines (and raw single-block
                        // reasoning like DeepSeek's) keep the FULL text so the
                        // expanded content starts with the same words as the
                        // collapsed preview instead of jumping mid-thought.
                        const reasoningContent = isStructured
                          ? reasoningBody
                          : reasoningFull
                        // Expandable whenever there's more to reveal than the
                        // single-line preview: a non-empty body, or a headline
                        // that had to be truncated.
                        const hasReasoningBody =
                          reasoningBody.trim().length > 0 || headlineTruncated
                        return (
                          <Reasoning
                            key={`${message.id}-${partIndex}`}
                            className='w-full'
                            isStreaming={isReasoningStreaming}
                            startTime={
                              isLatestReasoningPart ? reasoningStartTime : undefined
                            }
                          >
                            <ReasoningTrigger
                              summary={reasoningHeadline}
                              expandable={hasReasoningBody}
                            />
                            {hasReasoningBody && (
                              <ReasoningContent>
                                {reasoningContent}
                              </ReasoningContent>
                            )}
                          </Reasoning>
                        )
                      }

                      return null
                    }

                    const renderedTools = group.items
                      .map(({ part, index: partIndex }) =>
                        renderToolPart(part, message.id, partIndex, {
                          shouldPollTriggerGithubScan:
                            isLatestTriggerGithubScanMessage,
                          hasTextContent: false,
                          isLastAssistantMessage: isLastAssistant,
                        })
                      )
                      .filter(Boolean)

                    if (renderedTools.length === 0) return null

                    return (
                      <div key={`toolgroup-${message.id}-${groupIndex}`}>
                        {renderedTools}
                      </div>
                    )
                  })
                : parts.map(
                    (
                      part: UIMessagePart<UIDataTypes, UITools>,
                      partIndex: number
                    ) => {
                      if (part.type === 'text') {
                        const text = (part as { text?: string }).text
                        if (!text?.trim()) return null
                        return (
                          <ResponseWithCitations
                            key={`${message.id}-${partIndex}`}
                            content={text}
                            sources={sources}
                          />
                        )
                      }
                      if (part.type === 'data') {
                        const attachments = (part as any).data?.attachments as
                          | Array<{ name: string; type?: string }>
                          | undefined
                        if (!attachments?.length) return null
                        return (
                          <div
                            key={`${message.id}-${partIndex}`}
                            className='flex flex-wrap gap-1.5 pt-1'
                          >
                            {attachments.map((a, i) => (
                              <span
                                key={i}
                                className='bg-border inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-text-body'
                              >
                                {a.type?.startsWith('image/') ? (
                                  <Image className='h-3 w-3' />
                                ) : (
                                  <FileText className='h-3 w-3' />
                                )}
                                <span className='max-w-[140px] truncate'>
                                  {a.name}
                                </span>
                              </span>
                            ))}
                          </div>
                        )
                      }
                      return null
                    }
                  )}
              {/* Idle-gap "Thinking …" — the model finished a segment and is
                  working on the next, but nothing is streaming yet. Self-timed
                  (fresh elapsed per gap); unmounts the moment the next part
                  streams in. */}
              {showTrailingThinkingRow ? (
                <Reasoning
                  key={`${message.id}-thinking-row`}
                  className='mb-0 w-full [overflow-anchor:none]'
                  isStreaming
                >
                  <ReasoningTrigger summary={null} expandable={false} />
                </Reasoning>
              ) : null}
            </MessageContent>
          )}
          {!isAssistant && (
            <ChatAttachmentPreviewDialog
              file={previewFile}
              onClose={() => setPreviewFile(null)}
            />
          )}

          {/* Error messages set the SDK status to 'error' (not 'ready'), so the
              Retry affordance must NOT gate on `isReady` — it would never show.
              Only the last turn's error gets Retry, since `regenerate()` always
              re-runs the latest turn. */}
          {isAssistant &&
            isErrorMessage &&
            isLastAssistant &&
            !isStreamingMessage &&
            onRegenerate && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] leading-5 text-text-subtle">
                <span>Request did not finish.</span>
                <button
                  type="button"
                  onClick={onRegenerate}
                  className="inline-flex w-fit max-w-fit flex-none cursor-pointer items-center gap-1.5 rounded-sm border border-cobalt-border bg-card px-2 text-[12px] font-medium text-cobalt-strong transition-colors hover:bg-primary-subtle dark:hover:border-cobalt-strong/40 dark:hover:bg-cobalt-tonal-hover"
                >
                  <RefreshCw className="h-3 w-3" />
                  Retry
                </button>
              </div>
            )}
          {isAssistant &&
            !isErrorMessage &&
            visibleTextContent.length > 0 &&
            isReady &&
            !isStreamingMessage && (
              <MessageActions
                content={allTextContent}
                onRegenerate={onRegenerate}
                className={cn(
                  !isLastAssistant &&
                    'opacity-0 group-hover/assistant:opacity-100'
                )}
              />
            )}
        </div>
      </ChatStoppedContext.Provider>
    </Message>
  )
}, areChatMessageItemPropsEqual)

function areChatMessageItemPropsEqual(
  prev: ChatMessageItemProps,
  next: ChatMessageItemProps
) {
  return (
    prev.message.id === next.message.id &&
    prev.message.role === next.message.role &&
    prev.message.parts === next.message.parts &&
    prev.isLastAssistant === next.isLastAssistant &&
    prev.isLatestTriggerGithubScanMessage ===
      next.isLatestTriggerGithubScanMessage &&
    prev.isReady === next.isReady &&
    prev.isStreamingMessage === next.isStreamingMessage &&
    prev.isActiveResponse === next.isActiveResponse &&
    prev.sources === next.sources &&
    prev.chatEventScope === next.chatEventScope &&
    prev.onRegenerate === next.onRegenerate
  )
}

// ── Page-contextual suggestions ─────────────────────────────────────────────
// Moved to ./page-suggestions so lightweight surfaces (e.g. the dashboard) can
// import the constant without pulling in this heavy module. Re-exported here so
// existing `import { PAGE_SUGGESTIONS } from './messages'` callers keep working.
export { PAGE_SUGGESTIONS } from './page-suggestions'

// ── Virtualized list ───────────────────────────────────────────────────────
//
// Why a separate inner component: we need the StickToBottom context (only
// available inside <Conversation>) to feed the virtualizer's
// `getScrollElement`. Putting the hook inside `Messages` would also work,
// but pulling it into its own component keeps Messages's branchy render
// easy to read and lets `useVirtualizer` settle without the empty-state
// branch above it shifting the hooks order.

interface VirtualizedMessageListProps {
  messages: UIMessage[]
  lastAssistantMessageId: string | null
  latestTriggerGithubScanMessageId: string | null
  isReady: boolean
  status: MessagesProps['status']
  lastMessageId: string | undefined
  messageSources: MessagesProps['messageSources']
  chatEventScope: MessagesProps['chatEventScope']
  onRegenerate: MessagesProps['onRegenerate']
}

const VIRTUALIZED_THRESHOLD = 30
const ITEM_VERTICAL_GAP_PX = 20 // matches the previous space-y-5
const ESTIMATED_ITEM_PX = 220

function VirtualizedMessageList({
  messages,
  lastAssistantMessageId,
  latestTriggerGithubScanMessageId,
  isReady,
  status,
  lastMessageId,
  messageSources,
  chatEventScope,
  onRegenerate,
}: VirtualizedMessageListProps) {
  const { scrollRef } = useStickToBottomContext()
  const showGlobalStreamDots = useMemo(
    () => shouldShowGlobalStreamDots(messages, lastMessageId, status),
    [messages, lastMessageId, status],
  )

  // Below the threshold the virtualizer's measurement churn during streaming
  // costs more than it saves. Render the linear list directly — the existing
  // memo on ChatMessageItem already keeps re-renders cheap.
  const useVirtual = messages.length >= VIRTUALIZED_THRESHOLD

  const virtualizer = useVirtualizer({
    count: useVirtual ? messages.length : 0,
    getScrollElement: () => scrollRef?.current ?? null,
    estimateSize: () => ESTIMATED_ITEM_PX,
    overscan: 6,
    getItemKey: (index) => messages[index]?.id ?? index,
    // paddingBottom on the row already contributes to getBoundingClientRect().
    // Do not add ITEM_VERTICAL_GAP_PX again — that double-counts the gap.
    measureElement: (el) => el?.getBoundingClientRect().height ?? 0,
  })

  const renderItem = useCallback(
    (message: UIMessage, index: number) => (
      <ChatMessageItem
        key={message.id}
        message={message}
        isLastAssistant={message.id === lastAssistantMessageId}
        isLatestTriggerGithubScanMessage={
          message.id === latestTriggerGithubScanMessageId
        }
        isReady={isReady}
        isStreamingMessage={
          status === 'streaming' && message.id === lastMessageId
        }
        isActiveResponse={
          message.id === lastMessageId &&
          (status === 'streaming' || status === 'submitted')
        }
        isFirstInGroup={
          message.role === 'assistant' &&
          (index === 0 || messages[index - 1]?.role !== 'assistant')
        }
        sources={messageSources?.get(message.id) ?? EMPTY_SOURCES}
        chatEventScope={chatEventScope}
        onRegenerate={onRegenerate}
      />
    ),
    [
      lastAssistantMessageId,
      latestTriggerGithubScanMessageId,
      isReady,
      status,
      lastMessageId,
      messages,
      messageSources,
      chatEventScope,
      onRegenerate,
    ]
  )

  if (!useVirtual) {
    return (
      <>
        <div className='flex flex-col space-y-5'>
          {messages.map((message, index) => renderItem(message, index))}
        </div>
        {showGlobalStreamDots && (
          <div className='mt-5 flex h-5 items-center'>
            <BeautifulUIPixelGridLoader className='text-muted-foreground' />
            <span className='sr-only'>Loading</span>
          </div>
        )}
      </>
    )
  }

  const totalSize = virtualizer.getTotalSize()
  const items = virtualizer.getVirtualItems()

  return (
    <>
      <div
        style={{
          height: totalSize,
          position: 'relative',
        }}
      >
        {items.map((virtualItem) => {
          const message = messages[virtualItem.index]
          if (!message) return null
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
                paddingBottom: ITEM_VERTICAL_GAP_PX,
              }}
            >
              {renderItem(message, virtualItem.index)}
            </div>
          )
        })}
      </div>
      {showGlobalStreamDots && (
        <div className='flex h-5 items-center'>
          <BeautifulUIPixelGridLoader className='text-muted-foreground' />
          <span className='sr-only'>Loading</span>
        </div>
      )}
    </>
  )
}

// ── Messages ───────────────────────────────────────────────────────────────

export const Messages = memo(function Messages({
  messages = [],
  status,
  messageSources,
  chatEventScope,
  onRegenerate,
}: MessagesProps) {
  const lastAssistantMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i].id
    }
    return null
  }, [messages])
  const latestTriggerGithubScanMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const message = messages[i]
      if (message.role !== 'assistant') continue
      const parts = message.parts ?? []
      const hasTriggerGithubScan = parts.some((part) => {
        if (part.type === 'tool-triggerGithubScan') return true
        if (part.type === 'tool-invocation') {
          return (part as any).toolInvocation?.toolName === 'triggerGithubScan'
        }
        return false
      })
      if (hasTriggerGithubScan) return message.id
    }
    return null
  }, [messages])
  const lastMessageId = messages.at(-1)?.id
  const isReady = status === 'ready'

  return (
    <ConversationContent className='mx-auto w-full max-w-3xl px-4 pb-4'>
      {messages.length === 0 ? null : (
        <VirtualizedMessageList
          messages={messages}
          lastAssistantMessageId={lastAssistantMessageId}
          latestTriggerGithubScanMessageId={latestTriggerGithubScanMessageId}
          isReady={isReady}
          status={status}
          lastMessageId={lastMessageId}
          messageSources={messageSources}
          chatEventScope={chatEventScope}
          onRegenerate={onRegenerate}
        />
      )}
    </ConversationContent>
  )
})
